import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import BodyChart from '../components/BodyChartAnatomical';
import Combobox from '../components/Combobox';
import PatientIdentityHeader from '../components/PatientIdentityHeader';
import MedicalDocsUpload from '../components/MedicalDocsUpload';
import { Save, Printer, ClipboardList, CheckCircle2, CalendarDays } from 'lucide-react';
import PrintPicker from '../components/PrintPicker';
import PrintSignatureBlock from '../components/PrintSignatureBlock';
import MedicationAISummary from '../components/MedicationAISummary';
// Iter 132 — F-DAR notes card was previously mounted here in Part 1; moved
// to Part 2 (Objective Assessment) per user request. Field values still on
// the interview model for back-compat.
import useAutosave, { AutosaveIndicator } from '../hooks/useAutosave';

const SUBJECTIVE_GROUPS = [
  { title: 'Symptoms', items: [
    { id: 'sec-chief',      label: 'Chief complaint section',     kind: 'section' },
    { id: 'fld-complaint',  label: '• Chief complaint text',       kind: 'field' },
    { id: 'fld-pain',       label: '• VAS pain score',             kind: 'field' },
    { id: 'fld-pain-q',     label: '• Pain quality',               kind: 'field' },
  ]},
  { title: 'History', items: [
    { id: 'sec-history',    label: 'Onset / mechanism section',    kind: 'section' },
    { id: 'fld-onset',      label: '• Onset',                      kind: 'field' },
    { id: 'fld-mechanism',  label: '• Mechanism / cause',          kind: 'field' },
    { id: 'fld-duration',   label: '• Pain duration',              kind: 'field' },
    { id: 'fld-pattern',    label: '• Pattern',                    kind: 'field' },
  ]},
  { title: 'Modifiers', items: [
    { id: 'sec-agg-rel',    label: 'Aggravating / relieving',      kind: 'section' },
    { id: 'sec-previous',   label: 'Previous treatment',           kind: 'section' },
  ]},
  { title: 'Referral & meds', items: [
    { id: 'sec-referral',   label: 'Referral block',               kind: 'section' },
    { id: 'sec-meds',       label: 'Medications',                  kind: 'section' },
    { id: 'sec-surgical',   label: 'Surgical history',             kind: 'section' },
  ]},
  { title: 'Medical', items: [
    { id: 'sec-pmh',        label: 'Past medical history',         kind: 'section' },
    { id: 'sec-diseases',   label: 'Existing diseases',            kind: 'section' },
    { id: 'sec-screening',  label: 'Health screening',             kind: 'section' },
    { id: 'sec-diagnostic', label: 'Diagnostic tests',             kind: 'section' },
  ]},
  { title: 'Lifestyle & visualisation', items: [
    { id: 'sec-lifestyle',  label: 'Occupation / activity / goals', kind: 'section' },
    { id: 'sec-bodychart',  label: 'Body chart (pain map)',         kind: 'section' },
  ]},
];

const EMPTY = {
  patient_id: '',
  chief_complaint: '',
  pain_score: 5,
  onset: '',
  mechanism: '',
  duration: '',
  aggravating_factors: '',
  relieving_factors: '',
  pain_quality: '',
  pain_pattern: 'intermittent',
  previous_treatment: '',
  referral_source: '',
  referral_physician: '',
  referral_notes: '',
  medications: '',
  surgical_history: '',
  family_history: '',
  occupation: '',
  activity_level: '',
  goals: '',
  therapist_notes: '',
  diagnosis: '',
  icd10_codes: [],
  icd11_codes: [],
  icd9_codes: [],
  icf_codes: [],
  diseases: [],
  pain_points: [],
  // Past Medical History
  pmh_conditions: [],
  pmh_other: '',
  falls_last_year: null,
  falls_count: '',
  fractures_history: null,
  fractures_where: '',
  metal_implants: null,
  metal_implants_where: '',
  smoker: null,
  smoking_per_day: '',
  exercises_regularly: null,
  exercise_frequency: '',
  has_allergies: null,
  allergies_list: '',
  is_pregnant: null,
  diagnostic_tests: [],
  // Iter 68 — F-DAR (Focus/Data/Action/Response) ad-hoc notes
  fdar_focus: '',
  fdar_data: '',
  fdar_action: '',
  fdar_response: '',
};

const PMH_OPTIONS = [
  'High blood pressure', 'Heart condition', 'Stroke', 'Osteoporosis',
  'Peripheral Neuropathy', 'Seizures/epilepsy', 'Vision problems', 'Diabetes',
  'Hearing problems', 'Fainting/dizziness', 'Emphysema', 'Frequent or severe headaches',
  'Bowel/bladder problems', 'Cancer', 'Arthritis', 'Asthma',
];

const DIAGNOSTIC_TESTS = [
  'X-rays', 'MRI', 'CT scan', 'Bone scan',
  'EMG', 'Blood work', 'Bone density', 'Ultrasound',
];

export default function PatientInterview({ mode = 'admin' }) {
  const { patient_id: routePatientId } = useParams();
  const { user } = useAuth();
  // Staff (admin or clinician) get to see Part 2 + therapist-only fields.
  // Patient (mode='self') only sees Part 1 subjective + goals + body chart.
  const isStaff = user?.role === 'admin' || user?.role === 'clinician';
  const targetId = mode === 'self' ? user?.user_id : routePatientId;

  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showBookPrompt, setShowBookPrompt] = useState(false);
  const navigate = useNavigate();
  const [activeMarker, setActiveMarker] = useState(null);
  const [activeIntensity, setActiveIntensity] = useState(5);
  const [activeNote, setActiveNote] = useState('');
  const [activeType, setActiveType] = useState('pain');

  // Iter 69 — universal autosave (localStorage every 1.5 s). Resumes after
  // device sleep or page refresh. Draft is cleared on successful network save.
  const { lastSavedAt, restore, clear } = useAutosave(`interview:${targetId || 'new'}`, form);

  useEffect(() => {
    if (!targetId) return;
    const draft = restore();
    if (draft && typeof draft === 'object') {
      setForm(prev => ({ ...prev, ...draft, patient_id: targetId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  useEffect(() => {
    if (!targetId) return;
    if (isStaff) {
      api.get(`/patients/${targetId}`).then(r => setPatient(r.data)).catch(() => {});
      api.get(`/interviews/${targetId}`).then(r => {
        setForm(f => ({ ...EMPTY, ...r.data, patient_id: targetId, ...((f && Object.keys(f).length > Object.keys(EMPTY).length / 2) ? f : {}) }));
      });
    } else {
      api.get('/patients/me').then(r => setPatient(r.data));
      api.get('/interviews/me').then(r => setForm(f => ({ ...EMPTY, ...r.data, patient_id: targetId, ...((f && Object.keys(f).length > Object.keys(EMPTY).length / 2) ? f : {}) })));
    }
  }, [targetId, isStaff]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addMarker = (m) => {
    const next = { ...m, intensity: activeIntensity, note: activeNote, type: activeType };
    setForm(f => ({ ...f, pain_points: [...(f.pain_points || []), next] }));
  };
  const removeMarker = (m) => {
    // Iter 130 — Match by muscle+side (in addition to x/y/view) so left and
    // right markers on the same muscle are treated as independent rows.
    // Without the `side` predicate, removing the LEFT biceps also removes
    // the RIGHT biceps because both share x=50/y=60/view='front'/muscle='biceps'.
    setForm(f => ({ ...f, pain_points: f.pain_points.filter(p => !(
      p.x === m.x && p.y === m.y && p.view === m.view
      && p.muscle === m.muscle && (p.side || null) === (m.side || null)
    )) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/interviews', { ...form, patient_id: targetId, pain_score: parseInt(form.pain_score) || 0 });
      clear();
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      // Iter 138 — after a patient finishes their interview, nudge them
      // straight into booking their appointment.
      if (mode === 'self') setShowBookPrompt(true);
    } finally { setSaving(false); }
  };

  const doPrint = () => window.print();

  if (!targetId) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-8 max-w-6xl print:p-4 print:max-w-none">
      <div className="flex items-end justify-between mb-6 print:mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CLINICAL INTAKE</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Patient interview</h1>
          {patient && (
            <p className="text-fg-muted mt-2">
              {patient.full_name} {patient.date_of_birth ? `· DOB ${patient.date_of_birth}` : ''} {patient.country ? `· ${patient.country}` : ''}
            </p>
          )}
        </div>
        <div className="flex gap-2 print:hidden">
          {mode !== 'self' && (
            <PrintPicker
              storageKey="subjective"
              patientId={targetId}
              form="subjective"
              groups={SUBJECTIVE_GROUPS}
              onPrint={doPrint}
            />
          )}
          <AutosaveIndicator lastSavedAt={lastSavedAt} onClear={clear}/>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="interview-save">
            <Save className="w-4 h-4"/> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {saved && <div className="mb-4 badge-success">Saved.</div>}

      {showBookPrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="book-prompt-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6 text-center shadow-2xl">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3"/>
            <h3 className="font-heading font-bold text-lg mb-1">Interview saved</h3>
            <p className="text-sm text-fg-muted mb-5">
              Thanks — your answers help your clinician prepare. Would you like to book an appointment now?
            </p>
            <div className="flex justify-center gap-2">
              <button type="button" onClick={() => setShowBookPrompt(false)}
                      className="btn-secondary text-sm" data-testid="book-prompt-later">
                Not now
              </button>
              <button type="button" onClick={() => navigate('/patient/book')}
                      className="btn-primary text-sm inline-flex items-center gap-2" data-testid="book-prompt-book">
                <CalendarDays className="w-4 h-4"/> Book an appointment
              </button>
            </div>
          </div>
        </div>
      )}

      <PatientIdentityHeader patient={patient} suffix={mode === 'self' ? 'Part 1 · Subjective' : 'Part 1 · Subjective'}/>

      <div className="grid lg:grid-cols-12 gap-6 print:grid-cols-12">
        <div className="lg:col-span-7 print:col-span-7 bento-card print:p-3">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="w-4 h-4 text-brand" />
            <h3 className="font-heading font-bold">Part 1 · Subjective examination</h3>
            <span className="ml-auto text-[10px] text-fg-muted uppercase tracking-wider">Filled by patient</span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2 grid md:grid-cols-2 gap-3" data-print-section="sec-chief">
              <div data-print-field="fld-complaint" className="md:col-span-2">
                <Field label="Chief complaint" full>
                  <textarea className="input min-h-[60px]" value={form.chief_complaint || ''} onChange={e=>set('chief_complaint', e.target.value)} data-testid="i-complaint"/>
                </Field>
              </div>
              <div data-print-field="fld-pain">
                <Field label="VAS pain (0–10)">
                  <input type="number" min="0" max="10" className="input"
                          value={form.pain_score}
                          onChange={e=>set('pain_score', e.target.value)}
                          data-testid="i-pain"/>
                </Field>
              </div>
              <div data-print-field="fld-pain-q">
                <Field label="Pain quality">
                  <select className="input" value={form.pain_quality || ''} onChange={e=>set('pain_quality', e.target.value)}>
                    <option value="">—</option>
                    <option>Sharp</option><option>Dull</option><option>Burning</option>
                    <option>Aching</option><option>Throbbing</option><option>Stabbing</option>
                  </select>
                </Field>
              </div>
            </div>

            <div className="md:col-span-2 grid md:grid-cols-2 gap-3" data-print-section="sec-history">
              <div data-print-field="fld-onset">
                <Field label="Onset">
                  <input className="input" value={form.onset || ''} onChange={e=>set('onset', e.target.value)} placeholder="e.g. 2 weeks ago" />
                </Field>
              </div>
              <div data-print-field="fld-mechanism">
                <Field label="Mechanism / cause">
                  <input className="input" value={form.mechanism || ''} onChange={e=>set('mechanism', e.target.value)} />
                </Field>
              </div>
              <div data-print-field="fld-duration">
                <Field label="Pain duration">
                  <select className="input" value={form.duration || ''} onChange={e=>set('duration', e.target.value)} data-testid="i-duration">
                    <option value="">—</option>
                    <option value="all_day">All day</option>
                    <option value="few_hours">A few hours a day</option>
                    <option value="during_activity">During activity</option>
                    <option value="after_activity">After activity</option>
                  </select>
                </Field>
              </div>
              <div data-print-field="fld-pattern">
                <Field label="Pattern">
                  <select className="input" value={form.pain_pattern || 'intermittent'} onChange={e=>set('pain_pattern', e.target.value)}>
                    <option value="constant">Constant</option>
                    <option value="intermittent">Intermittent</option>
                    <option value="morning">Morning stiffness</option>
                    <option value="night">Night pain</option>
                  </select>
                </Field>
              </div>
            </div>

            <div className="md:col-span-2 grid md:grid-cols-2 gap-3" data-print-section="sec-agg-rel">
              <Field label="Aggravating factors" full>
                <textarea className="input" value={form.aggravating_factors || ''} onChange={e=>set('aggravating_factors', e.target.value)} />
              </Field>
              <Field label="Relieving factors" full>
                <textarea className="input" value={form.relieving_factors || ''} onChange={e=>set('relieving_factors', e.target.value)} />
              </Field>
            </div>

            <div className="md:col-span-2 grid md:grid-cols-2 gap-3" data-print-section="sec-previous">
              <Field label="Previous treatment" full>
                <input className="input" value={form.previous_treatment || ''} onChange={e=>set('previous_treatment', e.target.value)} />
              </Field>
            </div>

            <div className="md:col-span-2 grid md:grid-cols-2 gap-3" data-print-section="sec-referral">
              <Field label="Referral source" >
                <select
                  className="input"
                  value={form.referral_source || ''}
                  onChange={e=>set('referral_source', e.target.value)}
                  data-testid="i-ref-src"
                >
                  <option value="">— Select —</option>
                  <option value="self">Self-referral</option>
                  <option value="gp">GP / family doctor</option>
                  <option value="specialist">Specialist</option>
                  <option value="orthopaedic">Orthopaedic surgeon</option>
                  <option value="hospital">Hospital discharge</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              {/* Iter 132 — When the patient self-refers, skip the physician
                  fields entirely (nothing sensible to enter) and jump straight
                  to Medications. Non-self-referral keeps the physician +
                  notes fields visible. */}
              {form.referral_source && form.referral_source !== 'self' && (
                <>
                  <Field label="Referring physician">
                    <input className="input" value={form.referral_physician || ''} onChange={e=>set('referral_physician', e.target.value)} data-testid="i-ref-phys"/>
                  </Field>
                  <Field label="Referral notes" full>
                    <textarea className="input min-h-[60px]" value={form.referral_notes || ''}
                              onChange={e=>set('referral_notes', e.target.value)}
                              placeholder="Notes from the referring physician (diagnosis, request, restrictions, etc.)"
                              data-testid="i-ref-notes"/>
                  </Field>
                </>
              )}
            </div>

            <div className="md:col-span-2 grid md:grid-cols-2 gap-3" data-print-section="sec-meds">
              <Field label="Medications (prescribed, OTC, supplements)" full>
                <textarea className="input min-h-[60px]" value={form.medications || ''} onChange={e=>set('medications', e.target.value)}
                          placeholder="List each medication on a new line, including dose"
                          data-testid="meds-list"/>
                {mode !== 'self' && (
                  <MedicationAISummary medicationsText={form.medications || ''} patientId={targetId}/>
                )}
              </Field>
            </div>

            <div className="md:col-span-2 grid md:grid-cols-2 gap-3" data-print-section="sec-surgical">
              <Field label="Surgeries (including dates)" full>
                <textarea className="input min-h-[60px]" value={form.surgical_history || ''} onChange={e=>set('surgical_history', e.target.value)}
                          placeholder="e.g. Right ACL reconstruction — Mar 2022"
                          data-testid="surgeries-list"/>
              </Field>
            </div>

            {/* PMH + Existing conditions side-by-side */}
            <div className="md:col-span-2 grid md:grid-cols-2 gap-4">
              <div className="surface border border-soft rounded-lg p-4" data-testid="pmh-section" data-print-section="sec-pmh">
                <h3 className="font-heading font-semibold text-sm flex items-center gap-2 mb-1">
                  <ClipboardList className="w-4 h-4 text-brand"/> Past Medical History
                </h3>
                <p className="text-xs text-fg-muted mb-3">Select conditions that apply to you.</p>
                <PmhMultiSelect value={form.pmh_conditions || []} onChange={v=>set('pmh_conditions', v)} options={PMH_OPTIONS}/>
                <Field label="Other (please specify)" full>
                  <input className="input mt-2" value={form.pmh_other || ''} onChange={e=>set('pmh_other', e.target.value)} data-testid="pmh-other"/>
                </Field>
              </div>
              <div className="surface border border-soft rounded-lg p-4" data-print-section="sec-diseases">
                <h3 className="font-heading font-semibold text-sm flex items-center gap-2 mb-1">
                  <ClipboardList className="w-4 h-4 text-brand"/> Existing medical conditions / comorbidities
                </h3>
                <p className="text-xs text-fg-muted mb-3">Search for additional diagnosed conditions.</p>
                <Combobox endpoint="/lookup/diseases" value={form.diseases || []}
                          onChange={v=>set('diseases', v)}
                          label=""
                          placeholder="Search diseases (e.g. diabetes, asthma)…"
                          testid="i-diseases"/>
              </div>
            </div>

            <div className="md:col-span-2 surface border border-soft rounded-lg p-4" data-print-section="sec-screening">
              <h4 className="font-heading font-semibold text-sm mb-3">Health screening</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <YesNoWithDetail label="Falls in the past year?" value={form.falls_last_year}
                                 onChange={v=>set('falls_last_year', v)}
                                 detail={form.falls_count}
                                 onDetailChange={v=>set('falls_count', v)}
                                 detailLabel="About how many?"
                                 testid="falls"/>
                <YesNoWithDetail label="History of fractures?" value={form.fractures_history}
                                 onChange={v=>set('fractures_history', v)}
                                 detail={form.fractures_where}
                                 onDetailChange={v=>set('fractures_where', v)}
                                 detailLabel="Where?"
                                 testid="fractures"/>
                <YesNoWithDetail label="Metal implants?" value={form.metal_implants}
                                 onChange={v=>set('metal_implants', v)}
                                 detail={form.metal_implants_where}
                                 onDetailChange={v=>set('metal_implants_where', v)}
                                 detailLabel="Where?"
                                 testid="implants"/>
                <YesNoWithDetail label="Do you smoke?" value={form.smoker}
                                 onChange={v=>set('smoker', v)}
                                 detail={form.smoking_per_day}
                                 onDetailChange={v=>set('smoking_per_day', v)}
                                 detailLabel="How much per day?"
                                 testid="smoke"/>
                <YesNoWithDetail label="Do you exercise regularly?" value={form.exercises_regularly}
                                 onChange={v=>set('exercises_regularly', v)}
                                 detail={form.exercise_frequency}
                                 onDetailChange={v=>set('exercise_frequency', v)}
                                 detailLabel="How often?"
                                 testid="exercise"/>
                <YesNoWithDetail label="Known allergies?" value={form.has_allergies}
                                 onChange={v=>set('has_allergies', v)}
                                 detail={form.allergies_list}
                                 onDetailChange={v=>set('allergies_list', v)}
                                 detailLabel="Please list"
                                 testid="allergies"/>
                {/* Iter 132 — Pregnancy question only makes sense for
                    female patients. Hide entirely for male/other so the
                    intake stays clean. If the sex is unknown, we still
                    surface it (better safe than surprised). */}
                {(!patient?.sex || patient.sex === 'female') && (
                  <YesNo label="Pregnant or might be pregnant?" value={form.is_pregnant}
                         onChange={v=>set('is_pregnant', v)}
                         testid="pregnant"/>
                )}
              </div>
            </div>
            <div className="md:col-span-2 surface border border-soft rounded-lg p-4" data-testid="diagnostic-tests-section" data-print-section="sec-diagnostic">
              <h3 className="font-heading font-semibold text-sm flex items-center gap-2 mb-1">
                <ClipboardList className="w-4 h-4 text-brand"/> Diagnostic Tests
              </h3>
              <p className="text-xs text-fg-muted mb-3">Check any tests done for your current condition.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2">
                <CheckBoxRow label="None"
                             checked={(form.diagnostic_tests || []).includes('None')}
                             onChange={() => {
                               // Clicking "None" wipes all other selections (mutually exclusive).
                               if ((form.diagnostic_tests || []).includes('None')) {
                                 set('diagnostic_tests', []);
                               } else {
                                 set('diagnostic_tests', ['None']);
                               }
                             }}
                             testid="dx-none"/>
                {DIAGNOSTIC_TESTS.map(opt => (
                  <CheckBoxRow key={opt} label={opt} checked={(form.diagnostic_tests || []).includes(opt)}
                               onChange={() => {
                                 const cur = (form.diagnostic_tests || []).filter(x => x !== 'None');
                                 set('diagnostic_tests',
                                   cur.includes(opt)
                                     ? cur.filter(x => x !== opt)
                                     : [...cur, opt]);
                               }}
                               testid={`dx-${opt.toLowerCase().replace(/[^a-z]+/g,'-')}`}/>
                ))}
              </div>
              {/* Iter 68 — "Other" free-text. Anything typed here is appended to
                  diagnostic_tests when the user blurs the field, and persists
                  in the clinic-wide custom-test list (settings.custom_diagnostic_tests). */}
              <div className="mt-3 flex items-end gap-2">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1">Other (write-in)</div>
                  <input className="input !text-sm"
                         placeholder="e.g. NCS, Doppler, Tilt-table…"
                         value={form._other_dx_test || ''}
                         onChange={e => set('_other_dx_test', e.target.value)}
                         onKeyDown={async (e) => {
                           if (e.key === 'Enter') {
                             e.preventDefault();
                             const v = (form._other_dx_test || '').trim();
                             if (!v) return;
                             const cur = (form.diagnostic_tests || []).filter(x => x !== 'None');
                             set('diagnostic_tests', cur.includes(v) ? cur : [...cur, v]);
                             set('_other_dx_test', '');
                             try { await api.post('/clinic/custom-options', { kind: 'diagnostic_test', value: v }); } catch {}
                           }
                         }}
                         data-testid="dx-other-input"/>
                </div>
                <button type="button"
                        onClick={async () => {
                          const v = (form._other_dx_test || '').trim();
                          if (!v) return;
                          const cur = (form.diagnostic_tests || []).filter(x => x !== 'None');
                          set('diagnostic_tests', cur.includes(v) ? cur : [...cur, v]);
                          set('_other_dx_test', '');
                          try { await api.post('/clinic/custom-options', { kind: 'diagnostic_test', value: v }); } catch {}
                        }}
                        className="btn-secondary text-xs mb-0.5"
                        data-testid="dx-other-add">Add</button>
              </div>
              {(form.diagnostic_tests || []).filter(t => !['None', ...DIAGNOSTIC_TESTS].includes(t)).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dx-custom-list">
                  {(form.diagnostic_tests || []).filter(t => !['None', ...DIAGNOSTIC_TESTS].includes(t)).map((t, i) => (
                    <span key={`dx-c-${i}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-brand/10 border border-brand/30 text-brand">
                      {t}
                      <button onClick={() => set('diagnostic_tests', form.diagnostic_tests.filter(x => x !== t))}
                              className="hover:opacity-70" aria-label={`Remove ${t}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="md:col-span-2 grid md:grid-cols-2 gap-3" data-print-section="sec-lifestyle">
              <Field label="Occupation">
                <input className="input" value={form.occupation || ''} onChange={e=>set('occupation', e.target.value)} />
              </Field>
              <Field label="Activity level">
                <select className="input" value={form.activity_level || ''} onChange={e=>set('activity_level', e.target.value)}>
                  <option value="">—</option>
                  <option>Sedentary</option><option>Light</option><option>Moderate</option><option>Active</option><option>Athletic</option>
                </select>
              </Field>
              <Field label="Patient goals" full>
                <textarea className="input" value={form.goals || ''} onChange={e=>set('goals', e.target.value)} />
              </Field>
            </div>
            {/* Therapist diagnosis, ICD-10/-9 + ICF codes have moved to Part 2 — Objective Assessment / Diagnosis section. */}
          </div>
        </div>

        {/* Body chart — visible to both patient (Part 1 ends here) and staff.
            Iter 132 — Removed the pre-marker Intensity + Type controls. Patient
            just taps to mark; the clinician grades intensity + type in Part 2
            under Objective Examination. */}
        <div className="lg:col-span-5 print:col-span-5 bento-card print:p-3" data-print-section="sec-bodychart">
          <h3 className="font-heading font-bold mb-1">Body chart</h3>
          <p className="text-xs text-fg-muted mb-3">Tap the muscle where you feel pain. Left &amp; right are tracked separately — tap again to remove.</p>

          {isStaff && (
            <div className="print:hidden mb-3">
              <label className="text-[11px] text-fg-muted">Optional note (next marker)</label>
              <input className="input" value={activeNote} onChange={e=>setActiveNote(e.target.value)} placeholder="Referred pain, dermatome, etc." data-testid="marker-note" />
            </div>
          )}

          <BodyChart painPoints={form.pain_points} onAdd={addMarker} onRemove={removeMarker} />

          {form.pain_points?.length > 0 && (
            <div className="mt-4 space-y-1.5">
              <div key={`marker-count-${form.pain_points.length}`} className="text-xs font-semibold text-fg">Markers ({form.pain_points.length})</div>
              {form.pain_points.map((p, i) => (
                <div key={i} className="text-xs text-fg-muted flex items-center justify-between border-b border-soft py-1">
                  <span>
                    {p.side ? `${p.side.charAt(0).toUpperCase()}${p.side.slice(1)} ` : ''}
                    {(p.muscle || p.view || '').replace('-', ' ')}
                    {isStaff && p.intensity ? ` · VAS ${p.intensity}` : ''}
                    {isStaff && p.type && p.type !== 'pain' ? ` · ${p.type}` : ''}
                    {p.note ? ` · ${p.note}` : ''}
                  </span>
                  <button type="button" onClick={() => removeMarker(p)} className="text-[#B83A3A] print:hidden" data-testid={`del-marker-${i}`}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <MedicalDocsUpload patientId={targetId} isStaff={isStaff}/>
      </div>

      {/* Iter 132 — F-DAR notes moved to Part 2 (Objective examination). The
          <FDarNotesCard/> mount that used to live here has been removed.
          Form fields (`fdar_focus/data/action/response`) are still on the
          model so old records hydrate, and Part 2's Objective Assessment
          page now renders the card there. */}

      {isStaff && (
        <div className="mt-6 surface border border-soft rounded-lg p-5 flex flex-wrap items-center justify-between gap-3 print:hidden" data-testid="continue-objective-cta">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-1">Therapist next step</div>
            <h4 className="font-heading font-semibold">Part 2 · Objective examination</h4>
            <p className="text-xs text-fg-muted mt-1">Vitals, ROM, MMT, neurology, cardiovascular, 1RM, functional tests, LSI, AI suggestions.</p>
          </div>
          <a href={`/admin/patients/${targetId}/objective-assessment`}
             className="btn-primary inline-flex items-center gap-2"
             data-testid="open-objective-btn">
            Open Part 2 →
          </a>
        </div>
      )}

      {/* Print footer */}
      <div className="hidden print:block text-xs text-fg-muted mt-6 pt-3 border-t border-soft">
        Physiogenic · Patient interview · Generated {new Date().toLocaleString()}
      </div>
      <PrintSignatureBlock leftLabel="Clinician / Provider" rightLabel="Patient" />
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={full ? 'md:col-span-2 block' : 'block'}>
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

function CheckBoxRow({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-start gap-2 cursor-pointer text-sm hover:bg-[var(--bg-tertiary)] rounded px-1.5 py-1 transition">
      <input type="checkbox" checked={checked} onChange={onChange}
             className="mt-0.5 accent-[var(--brand)]"
             data-testid={testid}/>
      <span className="leading-tight">{label}</span>
    </label>
  );
}

function PmhMultiSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const toggle = (opt) => onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={()=>setOpen(o=>!o)}
              className="input w-full text-left flex items-center justify-between gap-2"
              data-testid="pmh-multiselect">
        <span className="truncate">
          {value.length === 0
            ? <span className="text-fg-muted">Select conditions…</span>
            : `${value.length} selected`}
        </span>
        <span className="text-fg-muted text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map(v => (
            <span key={v} className="inline-flex items-center gap-1 bg-brand/10 text-brand text-xs px-2 py-0.5 rounded-full">
              {v}
              <button type="button" onClick={()=>toggle(v)} className="hover:text-[#B83A3A]" data-testid={`pmh-rm-${v.toLowerCase().replace(/[^a-z]+/g,'-').slice(0,30)}`}>×</button>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 surface border border-soft rounded-lg shadow-lg max-h-64 overflow-y-auto p-1" data-testid="pmh-options">
          {options.map(opt => (
            <label key={opt} className="flex items-center gap-2 px-2 py-1.5 hover:bg-[var(--bg-tertiary)] rounded cursor-pointer text-sm">
              <input type="checkbox" checked={value.includes(opt)} onChange={()=>toggle(opt)}
                     className="accent-[var(--brand)]"
                     data-testid={`pmh-opt-${opt.toLowerCase().replace(/[^a-z]+/g,'-').slice(0,30)}`}/>
              <span>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div className="text-sm">
      <div className="text-fg-secondary mb-1.5">{label}</div>
      <div className="inline-flex rounded-md border border-soft overflow-hidden">
        <button type="button" onClick={()=>onChange(true)}
                className={`px-4 py-1.5 text-xs font-medium transition ${value === true ? 'bg-brand text-white' : 'bg-white text-fg-muted hover:bg-[var(--bg-tertiary)]'}`}
                data-testid={`${testid}-yes`}>Yes</button>
        <button type="button" onClick={()=>onChange(false)}
                className={`px-4 py-1.5 text-xs font-medium transition ${value === false ? 'bg-brand text-white' : 'bg-white text-fg-muted hover:bg-[var(--bg-tertiary)]'}`}
                data-testid={`${testid}-no`}>No</button>
        {value !== null && value !== undefined && (
          <button type="button" onClick={()=>onChange(null)}
                  className="px-2 text-[10px] text-fg-muted hover:text-fg"
                  title="Clear" data-testid={`${testid}-clear`}>×</button>
        )}
      </div>
    </div>
  );
}

function YesNoWithDetail({ label, value, onChange, detail, onDetailChange, detailLabel, testid }) {
  return (
    <div className="text-sm">
      <YesNo label={label} value={value} onChange={onChange} testid={testid}/>
      {value === true && (
        <input className="input mt-1.5 text-sm" placeholder={detailLabel}
               value={detail || ''} onChange={e=>onDetailChange(e.target.value)}
               data-testid={`${testid}-detail`}/>
      )}
    </div>
  );
}
